import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import { useTheme } from '@mui/material/styles';
import {
  AD as ADFlag,
  AL as ALFlag,
  AT as ATFlag,
  BA as BAFlag,
  BE as BEFlag,
  BG as BGFlag,
  CH as CHFlag,
  CY as CYFlag,
  CZ as CZFlag,
  DE as DEFlag,
  DK as DKFlag,
  EE as EEFlag,
  ES as ESFlag,
  FI as FIFlag,
  FO as FOFlag,
  FR as FRFlag,
  GB as GBFlag,
  GI as GIFlag,
  GR as GRFlag,
  HR as HRFlag,
  HU as HUFlag,
  IE as IEFlag,
  IL as ILFlag,
  IS as ISFlag,
  IT as ITFlag,
  LI as LIFlag,
  LT as LTFlag,
  LU as LUFlag,
  LV as LVFlag,
  MC as MCFlag,
  MD as MDFlag,
  ME as MEFlag,
  MK as MKFlag,
  MT as MTFlag,
  NL as NLFlag,
  NO as NOFlag,
  PL as PLFlag,
  PT as PTFlag,
  RO as ROFlag,
  SE as SEFlag,
  SI as SIFlag,
  SK as SKFlag,
  SM as SMFlag,
  SR as SRFlag,
  TH as THFlag,
  TR as TRFlag,
  UA as UAFlag,
} from 'country-flag-icons/react/3x2';
import React, { cloneElement, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import config from '../../../config';

import { phoneNumberCountryCodeLocalization } from '../../../util/phoneNumberUtils';

const countryOption = {
  AD: { component: ADFlag, country: 'Andorra', countryCode: 376 },
  AL: { component: ALFlag, country: 'Albania', countryCode: 355 },
  BA: { component: BAFlag, country: 'Bosnia and Herzegovina', countryCode: 387 },
  BE: { component: BEFlag, country: 'Belgium', countryCode: 32 },
  BG: { component: BGFlag, country: 'Bulgaria', countryCode: 359 },
  CY: { component: CYFlag, country: 'Cyprus', countryCode: 357 },
  CZ: { component: CZFlag, country: 'Czech Republic', countryCode: 420 },
  DK: { component: DKFlag, country: 'Denmark', countryCode: 45 },
  EE: { component: EEFlag, country: 'Estonia', countryCode: 372 },
  FI: { component: FIFlag, country: 'Finland', countryCode: 358 },
  FO: { component: FOFlag, country: 'Faroe Islands', countryCode: 298 },
  GB: { component: GBFlag, country: 'United Kingdom', countryCode: 44 },
  GI: { component: GIFlag, country: 'Gibraltar', countryCode: 350 },
  GR: { component: GRFlag, country: 'Greece', countryCode: 30 },
  HR: { component: HRFlag, country: 'Croatia', countryCode: 385 },
  HU: { component: HUFlag, country: 'Hungary', countryCode: 36 },
  IE: { component: IEFlag, country: 'Ireland', countryCode: 353 },
  IL: { component: ILFlag, country: 'Israel', countryCode: 972 },
  IS: { component: ISFlag, country: 'Iceland', countryCode: 354 },
  LI: { component: LIFlag, country: 'Liechtenstein', countryCode: 423 },
  LT: { component: LTFlag, country: 'Lithuania', countryCode: 370 },
  LU: { component: LUFlag, country: 'Luxembourg', countryCode: 352 },
  LV: { component: LVFlag, country: 'Latvia', countryCode: 371 },
  MC: { component: MCFlag, country: 'Monaco', countryCode: 377 },
  MD: { component: MDFlag, country: 'Moldova', countryCode: 373 },
  ME: { component: MEFlag, country: 'Montenegro', countryCode: 382 },
  MK: { component: MKFlag, country: 'Macedonia', countryCode: 389 },
  MT: { component: MTFlag, country: 'Malta', countryCode: 356 },
  NL: { component: NLFlag, country: 'Netherlands', countryCode: 31 },
  NO: { component: NOFlag, country: 'Norway', countryCode: 47 },
  PL: { component: PLFlag, country: 'Poland', countryCode: 48 },
  PT: { component: PTFlag, country: 'Portugal', countryCode: 351 },
  RO: { component: ROFlag, country: 'Romania', countryCode: 40 },
  SE: { component: SEFlag, country: 'Sweden', countryCode: 46 },
  SI: { component: SIFlag, country: 'Slovenia', countryCode: 386 },
  SK: { component: SKFlag, country: 'Slovakia', countryCode: 421 },
  SM: { component: SMFlag, country: 'San Marino', countryCode: 378 },
  SR: { component: SRFlag, country: 'Serbia', countryCode: 381 },
  TR: { component: TRFlag, country: 'Turkey', countryCode: 90 },
  UA: { component: UAFlag, country: 'Ukraine', countryCode: 380 },
  ...(config.env === 'development' ? { TH: { component: THFlag, country: 'Thailand', countryCode: 66 } } : {}),
};

const separateCountryOption = {
  DE: { component: DEFlag, country: 'Germany', countryCode: 49 },
  FR: { component: FRFlag, country: 'France', countryCode: 33 },
  IT: { component: ITFlag, country: 'Italy', countryCode: 39 },
  AT: { component: ATFlag, country: 'Austria', countryCode: 43 },
  CH: { component: CHFlag, country: 'Switzerland', countryCode: 41 },
  ES: { component: ESFlag, country: 'Spain', countryCode: 34 },
};

/** ISO codes of every country the phone input offers. */
export const PHONE_COUNTRY_CODES = Object.keys({ ...separateCountryOption, ...countryOption });

const DEFAULT_COUNTRY = 'DE';

const resolveCountry = (country) =>
  PHONE_COUNTRY_CODES.includes(country) ? country : DEFAULT_COUNTRY;

const compactPhone = (value) => (value || '').replace(/\s/g, '');

// '' or nothing but a (maybe still incomplete) dial code: '+', '+49', '+49 '.
const isBlankOrDialCode = (value) => /^(\+\d{0,4})?$/.test(compactPhone(value));

// After a defaultCountry change a blank field or a bare dial code follows the new country;
// on the first load of libphonenumber only a blank field is filled.
const shouldPrefill = (value, countryChanged) =>
  isBlankOrDialCode(value) && (countryChanged || !compactPhone(value));

/**
 * Lets the selected country follow `country` (the resolved defaultCountry) while the field
 * holds no real number yet, and with `prefillDialCode` writes the dial code into the field:
 * once when libphonenumber has loaded and the field is blank, and again whenever `country`
 * changes while the field is blank or holds only a dial code. A typed number is never touched,
 * and a field the user cleared stays blank.
 * @param {object} params
 * @param {string} params.country - resolved default country
 * @param {boolean} params.prefillDialCode
 * @param {object|null} params.libPhone - the lazily imported libphonenumber-js functions
 * @param {string} params.value - current raw field value
 * @param {string} params.selectedCountry
 * @param {(country: string) => void} params.setSelectedCountry
 * @param {(value: string) => void} params.onChange - the field's onChange
 */
const useDefaultCountry = ({
  country,
  prefillDialCode,
  libPhone,
  value,
  selectedCountry,
  setSelectedCountry,
  onChange,
}) => {
  const appliedCountry = useRef(country);

  // Runs only on load and on a defaultCountry change; typing must not re-trigger it.
  useEffect(() => {
    const countryChanged = appliedCountry.current !== country;
    appliedCountry.current = country;
    if (countryChanged && isBlankOrDialCode(value)) setSelectedCountry(country);
    if (!prefillDialCode || !libPhone || !shouldPrefill(value, countryChanged)) return;

    const dialCode = `+${libPhone.getCountryCallingCode(countryChanged ? country : selectedCountry)}`;
    if (compactPhone(value) !== dialCode) onChange(dialCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libPhone, country]);
};

const phoneNumberMenuItemStyle = {
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  flag: {
    display: 'inline-flex',
    width: '30px',
    height: '23px',
    marginRight: '10px',
    '& svg': {
      width: '100%',
      height: '100%',
    },
  },
  text: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
};

const PhoneNumberMenuItemComponent = ({ country, countryCode, component: Flag }) => {
  return (
    <Box sx={phoneNumberMenuItemStyle.wrapper}>
      <Box className="flag" sx={phoneNumberMenuItemStyle.flag}>
        <Flag alt={`${country} Flag`} title={country} />
      </Box>
      <Box className="text" sx={phoneNumberMenuItemStyle.text}>
        {`+${countryCode} ${country}`}
      </Box>
    </Box>
  );
};

PhoneNumberMenuItemComponent.displayName = 'PhoneNumberMenuItem';

const PhoneNumberMenuItem = memo(PhoneNumberMenuItemComponent);

const menuItemStyle = {
  item: {
    margin: 0,
  },
  divider: { opacity: 1, borderColor: '#000', width: '100%' },
};

const menuItems = [
  ...Object.entries(separateCountryOption).map(([key, value]) => {
    return (
      <MenuItem key={key} sx={menuItemStyle.item} value={key}>
        <PhoneNumberMenuItem
          component={value.component}
          country={phoneNumberCountryCodeLocalization[key]}
          countryCode={value.countryCode}
        />
      </MenuItem>
    );
  }),
  <MenuItem disabled key="divider" sx={{ opacity: 1 }} value="null">
    <Divider orientation="horizontal" sx={menuItemStyle.divider} />
  </MenuItem>,
  ...Object.entries(countryOption).map(([key, value]) => (
    <MenuItem key={key} sx={menuItemStyle.item} value={key}>
      <PhoneNumberMenuItem
        component={value.component}
        country={value.country}
        countryCode={value.countryCode}
      />
    </MenuItem>
  )),
].map((Item, index) => cloneElement(Item, { key: index }));

const selectStyle = {
  borderRadius: 0,
  boxShadow: 'none',
  '.MuiOutlinedInput-notchedOutline': { border: 0 },
  '.MuiOutlinedInput-notchedOutline:focus': { border: 0 },
  '& .MuiSelect-select': {
    display: 'flex',
    padding: 1,
    '& .text': {
      display: 'none',
    },
    '& .flag': {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      width: '30px',
      marginRight: 0,
    },
  },
};

const CustomAdornmentComponent = ({ countryCodeChanged, selectedCountry }) => {
  return (
    <InputAdornment position="start">
      <Select
        fullWidth
        onChange={countryCodeChanged}
        sx={selectStyle}
        value={selectedCountry}
        variant="outlined"
      >
        {menuItems}
      </Select>
    </InputAdornment>
  );
};

CustomAdornmentComponent.displayName = 'CustomAdornment';

const CustomAdornment = memo(CustomAdornmentComponent);

/**
 * Turns the digits the user entered into an E.164 number for the selected country.
 * National input ('15…', '015…') gets the dial code prepended, using the country's own
 * trunk-prefix rules (DE drops the 0, IT keeps it), also when typed after the dial code.
 * Input with another dial code, and input without significant digits yet ('', '0',
 * '+4'), stays as typed so the next keystroke can complete it.
 * @param {object} libPhone - the lazily imported libphonenumber-js functions
 * @param {string} phone - digits with an optional leading '+'
 * @param {string} country - ISO code of the selected country
 * @returns {string}
 */
const normalizePhoneInput = (libPhone, phone, country) => {
  const dialCode = `+${libPhone.getCountryCallingCode(country)}`;
  if (phone.startsWith('+') && !phone.startsWith(dialCode)) return phone;

  const formatter = new libPhone.AsYouType(country);
  formatter.input(phone.startsWith(dialCode) ? phone.slice(dialCode.length) : phone);
  return formatter.getNumber()?.number || phone;
};

/**
 * PhoneNumberInput component that provides a phone number input field with country selection
 * @param {object} props
 * @param {boolean} props.disabled - Whether the input is disabled
 * @param {boolean|string|null} props.error - Error state/message for the input
 * @param {boolean} props.fullWidth - Whether the input should take full width
 * @param {string} props.helperText - Helper text shown below the input
 * @param {string} props.id - ID for the input field
 * @param {string} props.label - Label text for the input
 * @param {string} props.name - Name attribute for the input
 * @param {string} props.placeHolder - Placeholder text for the input
 * @param {boolean} props.required - Whether the field is required
 * @param {'small'|'medium'|'large'} props.size - Size variant of the input
 * @param {object} props.inputProps - Additional props to pass to the input element
 * @param {string} [props.defaultCountry] - ISO code of the country selected initially, 'DE' by
 *   default and for unknown codes; while the field holds no real number yet, the selection
 *   follows later changes of this prop
 * @param {boolean} [props.prefillDialCode] - writes the dial code of the selected country into
 *   the blank field once libphonenumber has loaded (e.g. '+49'); off by default
 * @param {object} props.field - Field object containing value and onChange handler
 * @param {string} props.field.value - Current value of the input
 * @param {Function} props.field.onChange - Change handler function
 * @returns {React.JSX.Element}
 */
const PhoneNumberInputComponent = ({
  disabled = false,
  error = null,
  fullWidth = false,
  helperText = '',
  id,
  label = '',
  name,
  placeHolder = '',
  required = false,
  size = 'medium',
  inputProps = {},
  defaultCountry = DEFAULT_COUNTRY,
  prefillDialCode = false,
  field = {
    value: '',
    onChange: (param) => null,
  },
}) => {
  const theme = useTheme();
  const country = resolveCountry(defaultCountry);
  const [selectedCountry, setSelectedCountry] = useState(country);
  const [libPhone, setLibPhone] = useState(null);
  const [asYouType, setAsYouType] = useState(null);

  useDefaultCountry({
    country,
    prefillDialCode,
    libPhone,
    value: field.value,
    selectedCountry,
    setSelectedCountry,
    onChange: field.onChange,
  });

  useEffect(() => {
    (async () => {
      const { AsYouType, getCountryCallingCode, parseIncompletePhoneNumber } = await import('libphonenumber-js');
      setLibPhone({ AsYouType, getCountryCallingCode, parseIncompletePhoneNumber });
      setAsYouType(new AsYouType('DE'));
    })();
  }, []);

  useEffect(() => {
    if (field.value && libPhone) {
      const parsedPhone = getParsedPhoneNumber();
      if (parsedPhone && parsedPhone.countryCallingCode) {
        const country = Object.entries({ ...countryOption, ...separateCountryOption }).find(
          ([key, value]) => value.countryCode === parseInt(parsedPhone.countryCallingCode),
        );
        if (country) {
          setSelectedCountry(country[0]);
        }
      }
    }
  }, [field.value, libPhone]);

  const inputPhone = useCallback((phoneNumber) => {
    if (!libPhone || !asYouType) return phoneNumber;
    asYouType.reset();
    return asYouType.input(phoneNumber);
  }, [libPhone, asYouType]);

  field.value = inputPhone(field.value);

  const getParsedPhoneNumber = useCallback(() => {
    let parsed;

    if (asYouType && asYouType.getNumber) {
      parsed = asYouType.getNumber();
    }
    return parsed;
  }, [asYouType]);

  const getNewPhone = useCallback(
    (phone, newCountryCode) => {
      if (!libPhone) return phone;
      let newPhone = phone;
      const parsedPhone = getParsedPhoneNumber();

      if (!parsedPhone || newPhone.trim() === '') {
        return `+${newCountryCode}`;
      }

      newPhone =
        newCountryCode === '1'
          ? parsedPhone.nationalNumber
          : `+${newCountryCode}${parsedPhone.nationalNumber}`;

      return newPhone;
    },
    [libPhone, getParsedPhoneNumber],
  );

  const { onChange: fieldOnChange, ...fieldProps } = field;

  const countryCodeChanged = useCallback(
    (ev) => {
      const newCountry = ev.target.value;
      const code = libPhone ? libPhone.getCountryCallingCode(newCountry) : '';
      const newPhone = getNewPhone(field.value, code);

      if (newPhone !== field.value) {
        fieldOnChange(newPhone);
      }
      setSelectedCountry(newCountry);
    },
    [libPhone, field.value, fieldOnChange, getNewPhone],
  );

  const phoneNumberChanged = useCallback(
    (ev) => {
      if (!libPhone) return;
      const phone = libPhone.parseIncompletePhoneNumber(ev.target.value);
      // The flag follows the dial code via the field.value effect above.
      fieldOnChange(normalizePhoneInput(libPhone, phone, selectedCountry));
    },
    [libPhone, selectedCountry, fieldOnChange],
  );

  const startAdornment = useMemo(
    () => (
      <CustomAdornment countryCodeChanged={countryCodeChanged} selectedCountry={selectedCountry} />
    ),
    [countryCodeChanged, selectedCountry],
  );

  return (
    <FormControl error={!!error} fullWidth={fullWidth} required={required}>
      {label ? (
        <InputLabel htmlFor={id} shrink={false}>
          {label}
        </InputLabel>
      ) : null}
      <OutlinedInput
        disabled={disabled || !libPhone}
        error={!!error}
        id={id}
        name={name}
        onChange={phoneNumberChanged}
        placeholder={placeHolder}
        size={size}
        startAdornment={startAdornment}
        type="tel"
        {...fieldProps}
        {...inputProps}
      />
      {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
      {error ? <Box color={theme.palette.error.main}>{error}</Box> : null}
    </FormControl>
  );
};

PhoneNumberInputComponent.displayName = 'PhoneNumberInput';

const PhoneNumberInput = memo(PhoneNumberInputComponent);

export default PhoneNumberInput;